import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Buttons, CancelButton, Drawer } from '~/components';
import ReadForm from '~/components/read-only/ReadForm';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useApi, useWorkspace } from '~/contexts';
import { colors } from '~/styles';
import { entities, events, sources } from '../lookups';

const EmptyState = styled.div`
  padding: 1.5rem;
  color: ${colors.grey40};
  font-style: italic;
`;

// Read-only drawer showing a single workspace-history entry's full details. It loads its own data by
// id (plus the entity + event that identify which history table the row lives in) from the dedicated
// route, so it never depends on the report list row or its truncated notes. For a deleted invoice, the
// notes are the snapshot captured at deletion (see api/queries/invoices/add-deletion-history.js).
export default function HistoryItemDrawer({ historyItemId, entity, event, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ isReady, record }, setQuery] = useState({ isReady: false, record: null });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .reports()
        .historyItem(historyItemId)
        .get({ entity, event });
      setQuery({ isReady: true, record: data });
    } catch {
      setQuery({ isReady: true, record: null });
    }
  }, [api, workspace.id, historyItemId, entity, event]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Render the footer via the `actions` prop (not a Drawer.Actions child) so the content keeps its
  // bottom padding, matching the time-entry drawer. Drawer.Actions negatively margins that away.
  const footer = (closeDrawer) => (
    <Buttons align="right">
      <CancelButton onClick={() => closeDrawer()}>Close</CancelButton>
    </Buttons>
  );

  return (
    <Drawer
      isOpen
      title={entities[record?.entity]?.name ?? 'History Entry'}
      byline={events[record?.statusId]?.name}
      actions={footer}
      onClose={onClose}>
      {() => {
        if (!isReady) return null;
        if (!record) return <EmptyState>No details are available for this history entry.</EmptyState>;

        return (
          <ReadForm>
            <ReadForm.Section title="Event">
              <ReadForm.Control>
                <ReadTextbox label="Event Time" value={moment(record.eventTime).format('l LT')} />
                <ReadTextbox label="Event" value={events[record.statusId]?.name} />
              </ReadForm.Control>
              <ReadForm.Control>
                <ReadTextbox label="Action By" value={record.actionBy?.name} />
                <ReadTextbox label="Source" value={sources[record.sourceId]?.name} />
              </ReadForm.Control>
              {record.notes && (
                <ReadForm.Control>
                  <ReadTextbox label="Notes" value={record.notes} textarea />
                </ReadForm.Control>
              )}
            </ReadForm.Section>
          </ReadForm>
        );
      }}
    </Drawer>
  );
}
